import React, { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { useAuth } from "@/contexts/AuthContext";
import { PublicNavbar } from "@/components/layout/PublicNav";
import { AppFooter } from "@/components/layout/AppFooter";
import { GlowField } from "@/components/ui/GlowField";
import { CtaButton } from "@/components/ui/CtaButton";
import { SearchableSelect } from "@/components/ui/SearchableSelect";
import { PhoneInput, combinePhone } from "@/components/ui/PhoneInput";
import { COUNTRIES } from "@/lib/countries";
import { toast } from "sonner";
import { Building2, Sparkles } from "lucide-react";
import { motion } from "framer-motion";

export default function SignupInstitute() {
  const { signupInstitute } = useAuth();
  const nav = useNavigate();
  const [phone, setPhone] = useState({ country: "IN", number: "" });
  const [form, setForm] = useState({ full_name: "", institute_name: "", email: "", password: "", country: "IN" });
  const [loading, setLoading] = useState(false);
  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      await signupInstitute({ ...form, phone: combinePhone(phone) });
      toast.success("Institute account created");
      nav("/institute");
    } catch (e) {
      toast.error(e.response?.data?.detail || "Signup failed");
    } finally { setLoading(false); }
  };

  return (
    <div className="min-h-screen flex flex-col bg-[#FBFAF7] text-[#0A0C10]">
      <PublicNavbar />
      <div className="relative flex-1 flex items-center overflow-hidden">
        <div aria-hidden className="absolute inset-0 pointer-events-none">
          <div className="absolute -top-32 -left-16 w-[440px] h-[440px] rounded-full opacity-40" style={{ background: "radial-gradient(circle, rgba(255,180,0,0.30), transparent 60%)" }} />
          <div className="absolute -bottom-40 -right-16 w-[460px] h-[460px] rounded-full opacity-30" style={{ background: "radial-gradient(circle, rgba(0,47,167,0.20), transparent 60%)" }} />
        </div>
        <div className="relative w-full max-w-md mx-auto px-6 py-12">
          <div className="text-[11px] font-mono uppercase tracking-[0.22em] text-[#FFB400] inline-flex items-center gap-2 mb-3">
            <Building2 size={12} /> Institute / coaching centre
          </div>
          <h1 className="font-display font-bold text-4xl tracking-tighter">Run a multi-teacher institute.</h1>
          <p className="mt-2 text-neutral-600 text-[15px]">Invite teachers, share students, view central billing.</p>

          <motion.form
            onSubmit={submit}
            initial={{ opacity: 0, y: 12 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5, ease: [0.2, 0.7, 0.2, 1] }}
            className="bg-white border border-black/8 rounded-2xl shadow-[0_30px_80px_-20px_rgba(10,12,16,0.12)] mt-8 p-6 sm:p-8 space-y-4"
          >
            <GlowField label="Institute / centre name" testid="inst-name" required value={form.institute_name} onChange={(e) => set("institute_name", e.target.value)} placeholder="BrightStars Coaching" />
            <GlowField label="Your full name (admin)" testid="inst-admin-name" required value={form.full_name} onChange={(e) => set("full_name", e.target.value)} />
            <GlowField label="Admin email" testid="inst-email" type="email" required value={form.email} onChange={(e) => set("email", e.target.value)} />
            <GlowField label="Password" testid="inst-password" type="password" required minLength={6} value={form.password} onChange={(e) => set("password", e.target.value)} />
            <div>
              <div className="mb-1.5 text-[11px] font-mono uppercase tracking-[0.18em] text-neutral-500">Phone (optional)</div>
              <PhoneInput value={phone} onChange={setPhone} testid="inst-phone" />
            </div>
            <div>
              <div className="mb-1.5 text-[11px] font-mono uppercase tracking-[0.18em] text-neutral-500">Country</div>
              <SearchableSelect
                testid="inst-country"
                placeholder="Select country"
                options={COUNTRIES.map((c) => ({ value: c.code, label: c.name, hint: c.dial, icon: <span>{c.flag}</span> }))}
                value={form.country}
                onChange={(v) => set("country", v)}
              />
            </div>
            <CtaButton type="submit" variant="primary" size="lg" disabled={loading} className="w-full rounded-full" data-testid="inst-submit">
              <Sparkles size={14} /> {loading ? "Creating…" : "Create institute account"}
            </CtaButton>
          </motion.form>

          <div className="mt-6 text-sm text-neutral-600">Already have an account? <Link to="/login" className="text-[#0A0C10] font-semibold underline underline-offset-4 decoration-[#FFB400]/50 hover:text-[#FFB400] transition-colors">Sign in</Link></div>
        </div>
      </div>
      <AppFooter variant="light" />
    </div>
  );
}
